import { useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import DstButton from "../../components/DstButton/DstButton";
import DstLiveTable from "../../components/DstTable/DstLiveTable";
import ListerSearch from "../../components/Lister/ListerSearch/ListerSearch";
import MultiFilterDropdown from "../../components/MultiFilterDropdown/MultiFilterDropdown";
import MultiSelectLiveDropdown from "../../components/MultiSelectLiveDropdown/MultiSelectLiveDropdown";
import { useSession } from "../../contexts/SessionContext";
import { ProjectSubjectStatusEnum } from "../../models/ProjectSubjects";
import translate from "../../services/Translate";
import { completeNavigate } from "../../services/UtilityService";
import { generateRemoteUrl } from "./ProjectSubjects.function";
import CreateProjectSubjectModal from "./components/CreateProjectSubjectModal/CreateProjectSubjectModal";

const ProjectSubjects = () => {
  const { language } = useSession();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAuthors, setSelectedAuthors] = useState<number[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const _projectLanguageTags = [
    {
      value: "fr-FR",
      display: translate(language, "PAGES.PROJECT_SUBJECTS.LANGUAGES.FRENCH"),
    },
    {
      value: "en-US",
      display: translate(language, "PAGES.PROJECT_SUBJECTS.LANGUAGES.ENGLISH"),
    },
  ];

  const _projectStatusTags = Object.values(ProjectSubjectStatusEnum).map((status) => ({
    value: status,
    display: translate(language, `PAGES.PROJECT_SUBJECTS.STATUS_ENUM.${status}`),
  }));

  const columns = [
    {
      name: translate(language, "GLOBAL.ID"),
      key: "id",
      sortable: true,
    },
    {
      name: translate(language, "GLOBAL.NAME"),
      key: "short_name",
      sortable: true,
    },
    {
      name: translate(language, "GLOBAL.LANGUAGE"),
      key: "language",
      sortable: true,
    },
    {
      name: translate(language, "GLOBAL.AUTHOR"),
      key: "author",
      render: (item: any) => (
        <span
          className="text-primary clickable"
          onClick={(event) => completeNavigate(event, navigate, `/user/${item.author.id}`)}
        >
          {item.author.full_name}
        </span>
      ),
    },
    {
      name: translate(language, "PAGES.PROJECT_SUBJECTS.PROGRAM"),
      key: "program",
      sortable: true,
      render: (item: any) => (
        <div>{item.program ? <span>{item.program.name}</span> : <span className="text-muted">N/A</span>}</div>
      ),
    },
    {
      name: translate(language, "PAGES.PROJECT_SUBJECTS.DIFFICULTY"),
      key: "difficulty",
      render: (item: any) => (
        <div>{item.difficulty ? <span>{item.difficulty}</span> : <span className="text-muted">N/A</span>}</div>
      ),
    },
    {
      name: translate(language, "GLOBAL.STATUS"),
      key: "status",
    },
  ];

  return (
    <div className="p-3">
      <Row className="mb-3 align-items-center">
        <Col xl className="d-flex align-items-center">
          <h1 className="mb-0 me-3">{translate(language, "PAGES.PROJECT_SUBJECTS.TITLE")}</h1>
          <ListerSearch setSearchQuery={setSearchQuery} timeDelay={500} showSearchIcon />
        </Col>
        <Col xl className="text-end">
          <DstButton
            value={translate(language, "PAGES.PROJECT_SUBJECTS.BUTTON_CREATE")}
            variant="primary"
            clickFunction={() => setShowCreateModal(true)}
          />
        </Col>
      </Row>
      <Row className="mb-3 align-items-end">
        <Col className="d-flex flex-wrap align-items-center mb-2 gap-2">
          <MultiFilterDropdown
            inputTitle={translate(language, "GLOBAL.LANGUAGE")}
            tags={_projectLanguageTags}
            defaultValue={selectedLanguage}
            onSelectTags={(values) => setSelectedLanguage(values as string[])}
            buttonWidth="200px"
            language={language}
          />
          <MultiFilterDropdown
            inputTitle={translate(language, "GLOBAL.STATUS")}
            tags={_projectStatusTags}
            defaultValue={selectedStatus}
            onSelectTags={(values) => setSelectedStatus(values as string[])}
            showSearchBar
            buttonWidth="200px"
            language={language}
          />
          <MultiSelectLiveDropdown
            category={["users"]}
            label={
              selectedAuthors && selectedAuthors.length > 0
                ? `${selectedAuthors.length} ${translate(language, "PAGES.PROJECT_SUBJECTS.FILTERS.SELECTED_AUTHORS")}`
                : translate(language, "PAGES.PROJECT_SUBJECTS.FILTERS.SELECTED_AUTHORS")
            }
            language={language}
            mockClass="rounded-0 rounded-end"
            toggleClass="s-bold text-start d-flex border h-50px"
            onConfirm={(selectedUsers: any[]) => setSelectedAuthors(selectedUsers.map((user) => user.value.id))}
            showFilterButton={false}
            clearItemsOnSend={false}
          />
        </Col>
      </Row>
      <DstLiveTable
        key={refreshKey}
        classTable="text-nowrap"
        remote={generateRemoteUrl(searchQuery, selectedLanguage, selectedStatus, selectedAuthors)}
        remoteKey="project_subjects"
        columns={columns}
        pagination
        paginationAlwaysShowFooter
        paginationResizeOptions={[25, 50, 100]}
        itemsPerPage={50}
        striped
        defaultSortOrder="asc"
        onRowClick={(event, item) => completeNavigate(event, navigate, `/projects/subjects/${item.id}`)}
      />
      <CreateProjectSubjectModal
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={() => setRefreshKey((k) => k + 1)}
      />
    </div>
  );
};

export default ProjectSubjects;
